import { useCallback, useEffect, useRef, useState } from "react";
import { ErrorBanner } from "@/shared/ui";
import type { LogTail } from "./api";

// Il visualizzatore del log applicativo. Estratto da ImpostazioniPage
// (2026-08-27). La barra fissa in fondo («LOG · N job») e' stata tolta nel
// ridisegno del 28/10/2026: gli stessi numeri stanno in Impostazioni › Stato.
export function LogViewer({
  log,
  loading,
  error,
}: {
  log: LogTail;
  loading: boolean;
  error: string | null;
}) {
  const [autoScroll, setAutoScroll] = useState(true);
  const ref = useScrollToBottom(log.lines.length, autoScroll);

  // Senza questo ramo un 403 (o un backend giù) finiva sulla stessa stringa di
  // un log davvero vuoto: l'operatore concludeva «non c'è niente da vedere»
  // invece di segnalare il guasto.
  if (error) {
    return <ErrorBanner message={error} className="m-5" />;
  }

  if (loading && log.lines.length === 0) {
    return <p className="px-5 py-4 text-[13px] text-ink-2">Carico il log…</p>;
  }

  return (
    <div className="px-5 py-4 max-md:px-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[15px] font-bold text-ink max-md:text-[17px]">
          Log
          <span className="ml-2 break-all font-mono text-[11.5px] font-normal text-ink-2">
            {log.file || "—"}
          </span>
        </h3>
        <label className="flex min-h-8 items-center gap-2 text-[12.5px] text-ink max-md:min-h-11 max-md:text-[14px]">
          <input
            type="checkbox"
            checked={autoScroll}
            onChange={(event) => setAutoScroll(event.target.checked)}
            className="h-4 w-4 accent-[rgb(var(--om-ink))]"
          />
          Segui le righe nuove
        </label>
      </div>
      <pre
        ref={ref}
        className="max-h-[380px] overflow-auto rounded-md bg-[#1f1b16] p-3 font-mono text-[11.5px] leading-relaxed text-[#f2ece2] selection:bg-accent/40 max-md:max-h-[300px] max-md:text-[11px]"
      >
        {log.lines.length === 0 ? "Nessuna riga disponibile." : log.lines.join("\n")}
      </pre>
    </div>
  );
}

function useScrollToBottom(rowCount: number, enabled: boolean) {
  // Audit 2026-05-29 [LOW]: useRef stabile + callback-ref memoizzata.
  // Prima l'oggetto-ref era creato con useMemo (non garantisce persistenza)
  // e la callback-ref cambiava identita' a ogni render -> React staccava
  // (null) e riattaccava il nodo <pre> di continuo. Ora ``ref`` e' stabile
  // (useRef) e ``setRef`` lo e' anch'esso (useCallback su ``enabled``).
  const ref = useRef<HTMLPreElement | null>(null);
  const setRef = useCallback(
    (node: HTMLPreElement | null) => {
      ref.current = node;
      if (enabled && node) node.scrollTop = node.scrollHeight;
    },
    [enabled],
  );
  useEffect(() => {
    if (!enabled || !ref.current) return;
    // ``ref`` e' stabile (useRef) -> non va nelle deps.
    ref.current.scrollTop = ref.current.scrollHeight;
  }, [enabled, rowCount]);
  return setRef;
}
